/**
 * Tree-Metadata Synchronization Module
 * Based on Microreact's TREE_METADATA_SYNC.md documentation
 */

import { DataLoader } from '../data/data-loader';
import { ColorBrewerPalettes, getColorsFromPalette } from '../color/color-palettes';

interface TreeMetadataSyncState {
  labelField: string | null;
  rowsByLabel: Map<string, Array<{ row: Record<string, unknown>; rowIndex: number }>>;
  labelsByRow: Map<number, string[]>;
  selectedRows: Set<number>;
  // Active field for the metadata-block colour maps (colorMap / colorMapsByField).
  colorByField: string | null;
  // Active field for leaf tip colouring (leafColorMap). Owned solely by the leaf
  // subsystem so a metadata-block colour map for a different field cannot desync the
  // tip colours and collapse them to the grey fallback.
  leafColorByField: string | null;
  colorMap: Map<string, string>;
  leafColorMap: Map<string, string>;
  colorMapsByField: Map<string, Map<string, string>>;
  customColorMap: Map<string, string>;
  leafCustomColorMap: Map<string, string>;
  metadata: Record<string, unknown>[] | null;
  columnTypes: Record<string, unknown> | null;
  colorByEnabled: boolean;
  leafColorPalette: { category: string; name: string } | null;
  syncWithMetadata: boolean;
  hideNullBlocks: boolean;
}

export const TreeMetadataSync = {
  state: {
    labelField: null,
    rowsByLabel: new Map(),
    labelsByRow: new Map(),
    selectedRows: new Set(),
    colorByField: null,
    leafColorByField: null,
    colorMap: new Map(),
    leafColorMap: new Map(),
    colorMapsByField: new Map(),
    customColorMap: new Map(),
    leafCustomColorMap: new Map(),
    metadata: null,
    columnTypes: null,
    colorByEnabled: false,
    leafColorPalette: null,
    syncWithMetadata: false,
    hideNullBlocks: true,
  } as TreeMetadataSyncState,

  leafPaletteCategory: 'qualitative' as string,
  leafPaletteName: 'Set1' as string,
  currentPaletteCategory: 'qualitative' as string,
  currentPaletteName: 'Set1' as string,

  buildLabelMapping(tree: any, metadata: Record<string, unknown>[], labelField: string) {
    this.state.labelField = labelField;
    this.state.metadata = metadata;
    this.state.rowsByLabel.clear();
    this.state.labelsByRow.clear();

    const treeLabels = this.extractTreeLabels(tree);

    metadata.forEach((row, rowIndex) => {
      const label = row[labelField] as string | undefined;
      if (label && treeLabels.has(label)) {
        if (!this.state.rowsByLabel.has(label)) {
          this.state.rowsByLabel.set(label, []);
        }
        this.state.rowsByLabel.get(label)!.push({ row, rowIndex });

        if (!this.state.labelsByRow.has(rowIndex)) {
          this.state.labelsByRow.set(rowIndex, []);
        }
        this.state.labelsByRow.get(rowIndex)!.push(label);
      }
    });

    return this.state.rowsByLabel;
  },

  extractTreeLabels(tree: any): Set<string> {
    const labels = new Set<string>();
    if (tree.leaves) {
      tree.leaves.forEach((leaf: any) => {
        if (leaf.id) labels.add(leaf.id);
      });
    }
    return labels;
  },

  generateColorMap(metadata: Record<string, unknown>[], field: string, columnTypes: Record<string, unknown>) {
    if (!this.state.metadata) this.state.metadata = metadata;
    if (!this.state.columnTypes) this.state.columnTypes = columnTypes;

    const colorMap = new Map<string, string>();
    const values = DataLoader.getUniqueValues(metadata as any, field) as unknown[];

    const colors = this.getCategoricalColors(values.length);
    values.forEach((value, index) => {
      const customKey = `${field}:${value}`;
      const customColor = this.state.customColorMap.get(customKey);
      colorMap.set(String(value), customColor ?? colors[index] ?? '#cccccc');
    });

    this.state.colorMap = colorMap;
    this.state.colorByField = field;
    this.state.colorMapsByField.set(field, colorMap);

    return colorMap;
  },

  generateLeafColorMap(metadata: Record<string, unknown>[], field: string, columnTypes: Record<string, unknown>) {
    if (!this.state.metadata) this.state.metadata = metadata;
    if (!this.state.columnTypes) this.state.columnTypes = columnTypes;

    const colorMap = new Map<string, string>();
    const values = DataLoader.getUniqueValues(metadata as any, field) as unknown[];

    const colors = this.getLeafCategoricalColors(values.length);
    values.forEach((value, index) => {
      const leafCustomKey = `leaf:${field}:${value}`;
      const leafCustomColor = this.state.leafCustomColorMap.get(leafCustomKey);
      colorMap.set(String(value), leafCustomColor ?? colors[index] ?? '#cccccc');
    });

    this.state.leafColorMap = colorMap;
    this.state.leafColorByField = field;
    this.state.colorByField = field;
    this.state.colorMap = colorMap;

    console.log(`[TreeMetadataSync] Generated LEAF color map for field "${field}" with ${colorMap.size} values`);

    return colorMap;
  },

  getLeafCategoricalColors(count: number): string[] {
    if (ColorBrewerPalettes && this.leafPaletteCategory && this.leafPaletteName) {
      try {
        const colors = getColorsFromPalette(this.leafPaletteCategory as any, this.leafPaletteName as any, count) as string[] | null;
        if (colors && colors.length > 0) return colors;
      } catch (e) {
        // Fallback to default
      }
    }

    const palette = [
      '#1f77b4', '#ff7f0e', '#2ca02c', '#d62728', '#9467bd',
      '#8c564b', '#e377c2', '#7f7f7f', '#bcbd22', '#17becf',
      '#aec7e8', '#ffbb78', '#98df8a', '#ff9896', '#c5b0d5',
      '#c49c94', '#f7b6d2', '#c7c7c7', '#dbdb8d', '#9edae5',
    ];

    if (count <= palette.length) return palette.slice(0, count);

    const colors = [...palette];
    while (colors.length < count) colors.push(this.generateRandomColor());
    return colors;
  },

  setLeafPalette(category: string, name: string): void {
    this.leafPaletteCategory = category;
    this.leafPaletteName = name;
    console.log(`[TreeMetadataSync] Set leaf palette: ${category}/${name}`);
  },

  setLeafCustomColor(field: string, value: string, color: string): void {
    const key = `leaf:${field}:${value}`;
    this.state.leafCustomColorMap.set(key, color);
    if (this.state.leafColorByField === field && this.state.metadata) {
      this.generateLeafColorMap(this.state.metadata, field, this.state.columnTypes ?? {});
    }
  },

  getCategoricalColors(count: number): string[] {
    if (ColorBrewerPalettes && this.currentPaletteCategory && this.currentPaletteName) {
      try {
        const colors = getColorsFromPalette(this.currentPaletteCategory as any, this.currentPaletteName as any, count) as string[] | null;
        if (colors && colors.length > 0) return colors;
      } catch (e) {
        // Fallback to default
      }
    }

    const palette = [
      '#1f77b4', '#ff7f0e', '#2ca02c', '#d62728', '#9467bd',
      '#8c564b', '#e377c2', '#7f7f7f', '#bcbd22', '#17becf',
      '#aec7e8', '#ffbb78', '#98df8a', '#ff9896', '#c5b0d5',
      '#c49c94', '#f7b6d2', '#c7c7c7', '#dbdb8d', '#9edae5',
    ];

    if (count <= palette.length) return palette.slice(0, count);

    const colors = [...palette];
    while (colors.length < count) colors.push(this.generateRandomColor());
    return colors;
  },

  // Internal helper returning {r,g,b} used only by interpolateColor.
  // Named _hexToRgbObj to avoid collision with the public hexToRgb (array version) below.
  _hexToRgbObj(hex: string): { r: number; g: number; b: number } {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result
      ? { r: parseInt(result[1]!, 16), g: parseInt(result[2]!, 16), b: parseInt(result[3]!, 16) }
      : { r: 0, g: 0, b: 0 };
  },

  interpolateColor(color1: string, color2: string, factor: number): string {
    const c1 = this._hexToRgbObj(color1);
    const c2 = this._hexToRgbObj(color2);
    const r = Math.round(c1.r + (c2.r - c1.r) * factor);
    const g = Math.round(c1.g + (c2.g - c1.g) * factor);
    const b = Math.round(c1.b + (c2.b - c1.b) * factor);
    return this.rgbToHex(r, g, b);
  },

  rgbToHex(r: number, g: number, b: number): string {
    return '#' + [r, g, b].map(x => {
      const hex = x.toString(16);
      return hex.length === 1 ? '0' + hex : hex;
    }).join('');
  },

  generateRandomColor(): string {
    return '#' + Math.floor(Math.random() * 16777215).toString(16).padStart(6, '0');
  },

  setCustomColor(field: string, value: string, color: string): void {
    const key = `${field}:${value}`;
    this.state.customColorMap.set(key, color);
    if (this.state.colorByField === field && this.state.metadata) {
      this.generateColorMap(this.state.metadata, field, this.state.columnTypes ?? {});
    }
  },

  removeCustomColor(field: string, value: string): void {
    const key = `${field}:${value}`;
    this.state.customColorMap.delete(key);
    if (this.state.colorByField === field && this.state.metadata) {
      this.generateColorMap(this.state.metadata, field, this.state.columnTypes ?? {});
    }
  },

  clearCustomColorsForField(field: string): void {
    const keysToDelete: string[] = [];
    this.state.customColorMap.forEach((_color, key) => {
      if (key.startsWith(`${field}:`)) keysToDelete.push(key);
    });
    keysToDelete.forEach(key => this.state.customColorMap.delete(key));
    console.log(`[TreeMetadataSync] Cleared ${keysToDelete.length} custom colors for field: ${field}`);
  },

  clearAllCustomColors(): void {
    const count = this.state.customColorMap.size;
    this.state.customColorMap.clear();
    console.log(`[TreeMetadataSync] Cleared all ${count} custom colors`);
  },

  getCustomColorsForSave(): Record<string, string> {
    const obj: Record<string, string> = {};
    this.state.customColorMap.forEach((color, key) => { obj[key] = color; });
    return obj;
  },

  loadCustomColors(savedColors: Record<string, string> | null | undefined): void {
    this.state.customColorMap.clear();
    if (savedColors && typeof savedColors === 'object') {
      Object.entries(savedColors).forEach(([key, color]) => {
        this.state.customColorMap.set(key, color);
      });
    }
  },

  getLeafCustomColorsForSave(): Record<string, string> {
    const obj: Record<string, string> = {};
    this.state.leafCustomColorMap.forEach((color, key) => { obj[key] = color; });
    return obj;
  },

  loadLeafCustomColors(savedColors: Record<string, string> | null | undefined): void {
    this.state.leafCustomColorMap.clear();
    if (savedColors && typeof savedColors === 'object') {
      Object.entries(savedColors).forEach(([key, color]) => {
        this.state.leafCustomColorMap.set(key, color);
      });
    }
  },

  hasCustomColor(field: string, value: string): boolean {
    return this.state.customColorMap.has(`${field}:${value}`);
  },

  getColorForValue(field: string, value: string): string | null {
    const colorMap = this.state.colorMapsByField.get(field);
    return colorMap ? (colorMap.get(value) ?? null) : null;
  },

  computeNodeStyles(metadata: Record<string, unknown>[]): Record<string, { fillColor: string; strokeColor: string; shape: string; size: number }> {
    const nodeStyles: Record<string, { fillColor: string; strokeColor: string; shape: string; size: number }> = {};

    this.state.rowsByLabel.forEach((rowData, label) => {
      const { row } = rowData[0]!;
      let fillColor = '#999999';

      if (this.state.leafColorByField && this.state.leafColorMap.size > 0) {
        const value = row[this.state.leafColorByField];
        if (this.isEmptyValue(value)) {
          fillColor = '#000000';
        } else {
          fillColor = this.state.leafColorMap.get(String(value)) ?? fillColor;
        }
      }

      nodeStyles[label] = { fillColor, strokeColor: fillColor, shape: 'circle', size: 8 };
    });

    return nodeStyles;
  },

  isEmptyValue(value: unknown): boolean {
    if (value === null || value === undefined || value === '') return true;
    const strValue = String(value).trim().toLowerCase();
    return strValue === 'null' || strValue === 'n/a' || strValue === 'na' || strValue === '%null%';
  },

  computeMetadataBlocks(fields: string[]): Record<string, Record<string, { label: string; colour: string }>> {
    const metadata: Record<string, Record<string, { label: string; colour: string }>> = {};

    this.state.rowsByLabel.forEach((rowData, label) => {
      const { row } = rowData[0]!;
      metadata[label] = {};

      fields.forEach(field => {
        const value = row[field];

        if (this.isEmptyValue(value)) {
          if (this.state.hideNullBlocks) return;
          metadata[label]![field] = { label: '', colour: '#000000' };
          return;
        }

        const fieldColorMap = this.state.colorMapsByField.get(field);
        const color = fieldColorMap ? (fieldColorMap.get(String(value)) ?? '#cccccc') : '#cccccc';
        metadata[label]![field] = { label: String(value), colour: color };
      });
    });

    return metadata;
  },

  // Public hexToRgb: returns [r, g, b] array (this is the version that callers use)
  hexToRgb(hex: string): [number, number, number] {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result
      ? [parseInt(result[1]!, 16), parseInt(result[2]!, 16), parseInt(result[3]!, 16)]
      : [204, 204, 204];
  },

  /**
   * Leaf labels whose metadata row has `String(row[field]) === value`. The value
   * is compared stringified so a click on a legend swatch -- keyed by
   * `String(value)` -- selects exactly the leaves that swatch paints, including
   * numeric fields a strict `===` against the swatch's text label would miss.
   * Shared by both renderers' legend-click selection so a click picks the same
   * tips regardless of which renderer is active.
   */
  labelsForFieldValue(field: string, value: string): string[] {
    const labels: string[] = [];
    this.state.rowsByLabel.forEach((rowData, label) => {
      const row = rowData[0]?.row;
      if (row && String(row[field]) === value) labels.push(label);
    });
    return labels;
  },

  treeSelectionsToRows(selectedLeafIds: string[]): number[] {
    const rowIndices = new Set<number>();
    selectedLeafIds.forEach(leafId => {
      const rowData = this.state.rowsByLabel.get(leafId);
      if (rowData) rowData.forEach(({ rowIndex }) => rowIndices.add(rowIndex));
    });
    this.state.selectedRows = rowIndices;
    return Array.from(rowIndices);
  },

  rowSelectionsToTreeLeaves(selectedRowIndices: number[]): string[] {
    const leafIds = new Set<string>();
    selectedRowIndices.forEach(rowIndex => {
      const labels = this.state.labelsByRow.get(rowIndex);
      if (labels) labels.forEach(label => leafIds.add(label));
    });
    return Array.from(leafIds);
  },

  getMappedRows(): number[] {
    const mappedIndices = new Set<number>();
    this.state.labelsByRow.forEach((_labels, rowIndex) => { mappedIndices.add(rowIndex); });
    return Array.from(mappedIndices);
  },

  setColorByEnabled(enabled: boolean): void {
    this.state.colorByEnabled = enabled;
  },

  setLeafColorPalette(category: string, name: string): void {
    this.state.leafColorPalette = { category, name };
    this.currentPaletteCategory = category;
    this.currentPaletteName = name;
  },

  getLeafColorPalette(): { category: string; name: string } {
    return this.state.leafColorPalette ?? {
      category: this.currentPaletteCategory,
      name: this.currentPaletteName,
    };
  },

  setSyncWithMetadata(sync: boolean): void {
    this.state.syncWithMetadata = sync;
  },

  syncColorFromMetadata(field: string, paletteName: string, paletteCategory: string): void {
    if (!this.state.syncWithMetadata) return;
    if (this.state.colorByField === field && this.state.colorByEnabled) {
      this.setLeafColorPalette(paletteCategory, paletteName);
      if (this.state.metadata && this.state.columnTypes) {
        this.generateColorMap(this.state.metadata, field, this.state.columnTypes);
      }
    }
  },
};
